import { 
  Clock, 
  Home, 
  DollarSign, 
  Shield, 
  CheckCircle, 
  Users, 
  Building, 
  Briefcase, 
  TrendingUp,
  UserCheck,
  CreditCard,
  Wrench,
  FileX,
  ClipboardCheck,
  FileText,
  Star,
  BookOpen,
  Scale,
  Network,
  Monitor,
  Database,
  BarChart3,
  Building2
} from 'lucide-react';

export const mockData = {
  benefits: [
    {
      icon: Monitor,
      title: "Member Portal Access",
      description: "Exclusive access to our property management portal for tracking tenants, payments, and maintenance (Premium members)."
    },
    {
      icon: Scale,
      title: "Legal Protection",
      description: "Private contract law protection and compliance updates for Illinois property owners."
    },
    {
      icon: DollarSign,
      title: "Cost Efficiency",
      description: "Membership dues structure provides predictable costs and access to professional-grade tools."
    },
    {
      icon: Shield,
      title: "Private Association",
      description: "Operate within private membership rights and contractual relationships with full admin support."
    }
  ],

  services: [
    {
      icon: BookOpen,
      name: "Educational Resources & Legal Updates",
      description: "Compliance updates, educational materials, and access to member-exclusive forms and documents.",
      alaCarte: "Free",
      bundled: "Included",
      included: true
    },
    {
      icon: Wrench,
      name: "General Contractor & Skilled Laborers Network", 
      description: "Basic: FREE General Contractor & Skilled Laborers sign-up. Standard/Premium: Full access to network of licensed general contractors, specialty tradespeople, and pre-vetted vendors with maintenance coordination and referral services.",
      alaCarte: "Free Sign-up",
      bundled: "Full Access", 
      included: true,
      isHighlighted: true
    },
    {
      icon: Monitor,
      name: "Property Management Portal",
      description: "Complete portal access for tenant payment tracking, financial reporting, lease administration, and secure document storage.",
      alaCarte: "Standard+",
      bundled: "Included",
      included: true
    },
    {
      icon: FileText,
      name: "Administrative Support",
      description: "Professional administrative support for coordination and reporting through member portal.",
      alaCarte: "$100/mo",
      bundled: "$75/mo",
      included: true
    },
    {
      icon: FileX,
      name: "Eviction Support",
      description: "Administrative support for eviction processes and attorney referrals.",
      alaCarte: "$400",
      bundled: "$100",
      included: true,
      isPremiumOnly: true
    },
    {
      icon: BarChart3,
      name: "Reporting Tools",
      description: "Automated year-end reports with all rental income, expenses, and deductions organized for easy tax filing. No more scrambling for receipts.",
      alaCarte: "$200/year",
      bundled: "Included",
      included: true,
      isPremiumOnly: true
    },
    {
      icon: DollarSign,
      name: "Accounting Software Integration",
      description: "Seamless integration with popular accounting software for automated financial tracking and reporting.",
      alaCarte: "$150/year",
      bundled: "Included",
      included: true,
      isPremiumOnly: true
    },
    {
      icon: Building2,
      name: "Land Trust Education and Support",
      description: "Educational resources and support for setting up and managing land trusts for property privacy and asset protection.",
      alaCarte: "$300/year",
      bundled: "Included",
      included: true,
      isPremiumOnly: true
    }
  ],

  testimonials: [
    {
      name: "Robert Mitchell",
      role: "Private Property Owner",
      company: "Premium Member since 2023",
      content: "The member portal through Legacy Property Council has transformed how I manage my properties. Having all tenant information, payment history, and maintenance requests in one place is invaluable.",
      rating: 5,
      image: "https://images.pexels.com/photos/416405/pexels-photo-416405.jpeg"
    },
    {
      name: "Maria Santos",
      role: "Real Estate Investor",
      company: "Premium Member",
      content: "The portal access as a Premium member benefit is incredible. I can track all my properties, see tenant payment patterns, and coordinate maintenance through one dashboard while keeping my privacy rights.",
      rating: 5,
      image: "https://images.unsplash.com/photo-1758518727984-17b37f2f0562"
    },
    {
      name: "David Chen",
      role: "Property Portfolio Owner",
      company: "Standard Member",
      content: "Started with Standard membership for the vendor access, but the Premium portal upgrade is definitely worth it. The tenant tracking and payment history features are professional-grade tools.",
      rating: 5,
      image: "https://images.pexels.com/photos/416405/pexels-photo-416405.jpeg"
    }
  ],

  targetAudiences: [
    {
      icon: Building,
      title: "Property Owners & Investors",
      description: "Private property owners seeking professional tools and administrative support while maintaining full control and ownership rights",
      features: ["Portal access for tenant tracking", "Payment history monitoring", "Maintenance coordination", "Legal compliance updates"]
    },
    {
      icon: Users,
      title: "Busy Professionals & Entrepreneurs", 
      description: "High-achieving professionals who want property investment benefits without day-to-day management responsibilities",
      features: ["Automated administrative support", "Vendor network access", "Professional-grade reporting", "Time-saving coordination tools"]
    }
  ],

  faqs: [
    {
      question: "What is the Member Portal and who can access it?",
      answer: "The Member Portal is a comprehensive property management dashboard available to Standard ($50/month) and Premium ($100/month) members. It allows you to track tenant payments, view history, manage maintenance requests, and access all your property information in one secure location."
    },
    {
      question: "Can I see other members' tenant information in the portal?",
      answer: "No. The portal is designed with privacy protection - each member can only view their own properties and tenant information. Legacy Property Council administrators can access aggregate data for member support purposes only."
    },
    {
      question: "What's the difference between PMA membership and traditional property management?",
      answer: "As a PMA, we provide portal access and administrative coordination to members under private contract law. We don't act as property managers in public commerce - instead, we provide tools and support services exclusively to our private members."
    },
    {
      question: "How are membership dues structured?",
      answer: "We offer three membership levels: Basic (FREE) includes educational resources, legal updates, and General Contractor/specialty skills network. Standard ($50/month) adds maintenance coordination, vendor access, and Member Portal access. Premium ($100/month) includes enhanced portal features plus full administrative support, eviction coordination, comprehensive reporting tools that organize all your rental income and expenses for easy tax filing, accounting software integration feature, and Land Trust Education and Support."
    },
    {
      question: "Do I need to be a Premium member to access the portal?",
      answer: "No. Portal access is available to both Standard ($50/month) and Premium ($100/month) members. Standard members get full portal functionality for tenant tracking and payment management. Premium members receive enhanced portal features plus dedicated administrative support, comprehensive reporting tools with all income/expenses organized for tax filing, Land Trust Education and Support, and eviction coordination."
    },
    {
      question: "What legal protections does PMA membership provide?",
      answer: "PMA membership operates under private contract law with internal dispute resolution. Members agree to resolve disputes through private arbitration rather than public courts, to the fullest extent allowed by law."
    },
    {
      question: "Can I upgrade or downgrade my membership level?",
      answer: "Yes, membership levels can be adjusted based on your needs. Portal access is available to both Standard ($50/month) and Premium ($100/month) members, with Premium members receiving enhanced portal features plus dedicated administrative support."
    },
    {
      question: "How does the portal handle tenant payment tracking?",
      answer: "The portal provides comprehensive payment tracking including on-time payments, late fees, payment history, and automated reporting. You maintain full control and privacy over your tenant relationships while having professional tracking tools."
    }
  ],

  comparison: {
    traditional: {
      name: "Traditional Chicago Property Management",
      pricing: {
        managementFee: "8-12% of monthly rent",
        leasing: "$300-800 per placement",
        renewal: "$150-400 per renewal", 
        setup: "$200-500 initial setup",
        inspection: "$75-150 per inspection",
        eviction: "$500-2000+ legal fees"
      },
      features: {
        rentCollection: { available: true, details: "Manual collection, 3-5 day delays" },
        maintenanceCoordination: { available: true, details: "Phone/email coordination, markup on repairs" },
        leaseTracking: { available: true, details: "Basic tracking, limited access" },
        inspections: { available: true, details: "Scheduled inspections, additional fees" },
        evictionHandling: { available: true, details: "Full service, high legal fees" },
        compliance: { available: true, details: "Basic compliance, reactive updates" },
        support: { available: true, details: "Business hours only, account managers" },
        transparency: { available: false, details: "Limited financial transparency" },
        automation: { available: false, details: "Manual processes, paper-based" },
        ownership: { available: false, details: "Loss of direct tenant relationships" }
      }
    },
    lpa: {
      name: "Legacy Property Association",
      pricing: {
        standard: "$50/month flat rate",
        premium: "$100/month flat rate", 
        leasing: "Included in membership",
        renewal: "Included in membership",
        setup: "Free setup",
        inspection: "Member coordination tools",
        eviction: "$100 coordination (Premium)"
      },
      features: {
        rentCollection: { available: true, details: "Automated tracking, real-time updates" },
        maintenanceCoordination: { available: true, details: "Digital platform, vetted contractor network" },
        leaseTracking: { available: true, details: "Full digital access, document storage" },
        inspections: { available: true, details: "Self-managed with digital tools" },
        evictionHandling: { available: true, details: "Process coordination, $100 flat fee (Premium)" },
        compliance: { available: true, details: "Proactive updates, member education" },
        support: { available: true, details: "Administrative coordination, member portal" },
        transparency: { available: true, details: "Full financial transparency and control" },
        automation: { available: true, details: "Automated reporting, digital workflows" },
        ownership: { available: true, details: "Maintain direct tenant relationships" }
      }
    },
    breakEvenPoints: [
      { rentAmount: 1000, traditional8: 80, traditional12: 120, lpaStandard: 50, lpaPremium: 100 },
      { rentAmount: 1250, traditional8: 100, traditional12: 150, lpaStandard: 50, lpaPremium: 100 },
      { rentAmount: 1500, traditional8: 120, traditional12: 180, lpaStandard: 50, lpaPremium: 100 },
      { rentAmount: 2000, traditional8: 160, traditional12: 240, lpaStandard: 50, lpaPremium: 100 },
      { rentAmount: 2500, traditional8: 200, traditional12: 300, lpaStandard: 50, lpaPremium: 100 },
      { rentAmount: 3000, traditional8: 240, traditional12: 360, lpaStandard: 50, lpaPremium: 100 }
    ]
  }
};